/*
Tactile + visual confirmation that the capture went through.

Two signals fired in parallel from the capture-success branch:
- `navigator.vibrate(50)` — short single buzz. Silently no-ops on
  iOS Safari (the API is a Chromium-only thing). On Android Chrome
  the user feels a single tap that mirrors a real shutter button.
- A 220 ms full-screen white wash that fades to transparent. Does
  duty as a visual "shutter" on iOS where the haptic is missing,
  and stacks with the haptic on Android for the full sensation.

The flash element is appended directly to `<body>` (not `#app`) so
it survives the router's per-screen `innerHTML = ''` if a navigation
happens to fire on the same tick. It removes itself on
`transitionend`, with a setTimeout fallback for the rare case
transitionend gets coalesced away by the browser.
*/

const kFlashDurationMs: number = 240   // matches the CSS transition; cleanup fallback runs at +60 ms
const kHapticDurationMs: number = 50   // single short buzz, mirrors the Android Phase 12 capture haptic

/*
Fire both feedback signals. Cheap; safe to call from any successful
capture handler. Never throws — both APIs swallow rejections in their
own implementations and we tolerate the absence of either.
*/
export function triggerCaptureFeedback(): void
{
	flashScreen()
	triggerHaptic()
}

/*
One-shot vibration. Browser support is patchy: Android Chrome / Edge
respect it, iOS Safari + Firefox iOS ignore it silently. We don't
guard for the iOS case beyond the API existence check — the no-op
is the desired fallback.
*/
function triggerHaptic(): void
{
	if (typeof navigator === 'undefined') return
	if (typeof navigator.vibrate !== 'function') return
	try { navigator.vibrate(kHapticDurationMs) }
	catch { /* iOS may throw "NotSupportedError" inside a sandboxed standalone PWA — non-fatal */ }
}

/*
Visual shutter — append a fixed-position white wash, force a paint
at full opacity, then transition to 0 in the next frame. Removes
itself when the transition ends.
*/
function flashScreen(): void
{
	if (typeof document === 'undefined') return
	const vEl = document.createElement('div')
	vEl.className = 'capture-flash'
	document.body.appendChild(vEl)

	let vRemoved = false  // single-fire guard for the cleanup path
	const vCleanup = (): void => {
		if (vRemoved) return
		vRemoved = true
		vEl.remove()
	}
	vEl.addEventListener('transitionend', vCleanup, { once: true })
	// Fallback: if the browser coalesces the transitionend (common when
	// the page is hidden mid-animation) we still want the element gone.
	window.setTimeout(vCleanup, kFlashDurationMs + 60)

	// Two-frame trick: the element is inserted at opacity 1 (CSS default
	// in `.capture-flash`); we wait for one paint, then add the
	// `-fading` class which transitions opacity to 0. Without the rAF
	// the browser collapses both states into the final one and skips
	// the transition entirely.
	requestAnimationFrame(() => {
		requestAnimationFrame(() => vEl.classList.add('capture-flash-fading'))
	})
}
